import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AppearanceConfig = {
  primary?: string;
  secondary?: string;
  accent?: string;
  destructive?: string;
  displayFont?: string;
  bodyFont?: string;
  baseFontSize?: string; // px
};

const FONT_STACKS: Record<string, string> = {
  "facebook": `system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
  "system": `system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
  "space-grotesk": `"Space Grotesk", system-ui, sans-serif`,
  "inter": `"Inter", system-ui, sans-serif`,
  "poppins": `"Poppins", system-ui, sans-serif`,
  "montserrat": `"Montserrat", system-ui, sans-serif`,
  "roboto": `"Roboto", system-ui, sans-serif`,
  "open-sans": `"Open Sans", system-ui, sans-serif`,
  "lato": `"Lato", system-ui, sans-serif`,
  "segoe-ui": `"Segoe UI", system-ui, sans-serif`,
};

export const FONT_OPTIONS = [
  { value: "facebook", label: "Facebook (System)" },
  { value: "system", label: "System Default" },
  { value: "segoe-ui", label: "Segoe UI" },
  { value: "inter", label: "Inter" },
  { value: "space-grotesk", label: "Space Grotesk" },
  { value: "poppins", label: "Poppins" },
  { value: "montserrat", label: "Montserrat" },
  { value: "roboto", label: "Roboto" },
  { value: "open-sans", label: "Open Sans" },
  { value: "lato", label: "Lato" },
];

export const FACEBOOK_BLACK = "#050505";

function hexToHsl(hex: string): string | null {
  const m = hex.replace("#", "").trim();
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(m)) return null;
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyAppearance(cfg: AppearanceConfig) {
  const root = document.documentElement;
  const setVar = (name: string, hex?: string) => {
    if (!hex) return;
    const hsl = hexToHsl(hex);
    if (hsl) root.style.setProperty(name, hsl);
  };
  setVar("--primary", cfg.primary);
  setVar("--secondary", cfg.secondary);
  setVar("--accent", cfg.accent);
  setVar("--destructive", cfg.destructive);
  setVar("--ring", cfg.primary);

  // Foreground / text color: when "facebook" font is active, force Facebook black
  if (cfg.bodyFont === "facebook" || cfg.displayFont === "facebook") {
    const fbHsl = hexToHsl(FACEBOOK_BLACK);
    if (fbHsl) {
      root.style.setProperty("--foreground", fbHsl);
      root.style.setProperty("--card-foreground", fbHsl);
      root.style.setProperty("--popover-foreground", fbHsl);
    }
  }

  // Font stacks injected via a managed style tag
  const STYLE_ID = "appearance-fonts";
  let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_ID;
    document.head.appendChild(tag);
  }
  const body = FONT_STACKS[cfg.bodyFont || ""] || "";
  const display = FONT_STACKS[cfg.displayFont || ""] || body;
  const size = cfg.baseFontSize ? `${cfg.baseFontSize}px` : "";
  tag.textContent = `
    ${body ? `body, button, input, textarea, select { font-family: ${body} !important; }` : ""}
    ${display ? `h1,h2,h3,h4,h5,h6,.font-display { font-family: ${display} !important; }` : ""}
    ${size ? `body { font-size: ${size} !important; }` : ""}
  `;
}

export default function AppearanceLoader() {
  const { data } = useQuery({
    queryKey: ["site_appearance"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "site_appearance")
        .maybeSingle();
      if (!data?.value) return null;
      try { return JSON.parse(String(data.value)) as AppearanceConfig; } catch { return null; }
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data) applyAppearance(data);
  }, [data]);

  return null;
}